import { createSlice } from '@reduxjs/toolkit';
import ENDPOINTS from 'tradera-constants/endpoints';
import {
    axiosWithTokenRefresh,
    axiosConfigs,
    finalizeResponse,
    handleError
} from 'tradera-utils/api';

import {
    selectBox,
    isLoadingShippingProducts,
    selectIsBoxValid
} from './selectors';

const defaultBox = {
    length: 15,
    height: 15,
    width: 15,
    weight: 1000
};

const defaultCountry = 'SE';

const shippingCalculatorSlice = createSlice({
    name: 'shippingCalculator',
    initialState: {
        box: { ...defaultBox },
        selectedToCountry: defaultCountry,
        selectedFromCountry: defaultCountry,
        availableFromCountryCodes: [],
        availableToCountryCodes: [],
        defaultBox,
        weightUnit: 'kg',
        isLoadingShippingProducts: false,
        shippingProviders: [
            {
                providerName: 'SchenkerPrivpak'
            },
            {
                providerName: 'PostNordParcel'
            },
            {
                providerName: 'PostNordStamp'
            },
            {
                providerName: 'DHL'
            },
            {
                providerName: 'Bussgods'
            }
        ],
        internationalProductsGroupedByFromThenToCountry: {
            SE: { SE: [] }
        },
        showError: false
    },
    reducers: {
        setSelectedToCountry: (state, action) => {
            state.selectedToCountry = action.payload.selectedToCountry;
        },
        setSelectedFromCountry: (state, action) => {
            state.selectedFromCountry = action.payload.selectedFromCountry;
        },
        setDefaultValuesOnBox: state => {
            state.box = { ...defaultBox };
        },
        updateFilter: (state, action) => {
            const { filterName, value } = action.payload;
            state.box[filterName] = value;
        },
        setWeightFilter: (state, action) => {
            const map = {
                1: 50,
                2: 100,
                3: 250,
                4: 500,
                5: 1000,
                6: 2000,
                7: 3000,
                8: 5000,
                9: 7000,
                10: 9000,
                11: 10000,
                12: 12000,
                13: 15000,
                14: 20000,
                15: 35000
            };

            const value = map[action.payload.value];
            state.weightUnit = value % 1000 === 0 ? 'kg' : 'g';
            state.box.weight = value;
        },
        setWeightUnit: (state, action) => {
            state.weightUnit = action.payload.weightUnit;
        },
        beginFetchingShippingProducts: state => {
            state.isLoadingShippingProducts = true;
        },
        setShowError: state => {
            state.showError = true;
        },
        setShippingProducts: (state, action) => {
            const { internationalShippingCalculatorProducts } = action.payload;

            const internationalProductsGroupedByFromThenToCountry = internationalShippingCalculatorProducts.reduce(
                (prev, product) => {
                    const {
                        fromCountryCodeIso2: from,
                        toCountryCodeIso2: to
                    } = product;
                    prev[from] = prev[from] || {};
                    prev[from][to] = prev[from][to] || [];
                    prev[from][to].push(product);
                    return prev;
                },
                {}
            );

            const availableToCountryCodes = [
                ...new Set(
                    internationalShippingCalculatorProducts.map(
                        ({ toCountryCodeIso2 }) => toCountryCodeIso2
                    )
                )
            ];

            const availableFromCountryCodes = [
                ...new Set(
                    internationalShippingCalculatorProducts.map(
                        ({ fromCountryCodeIso2 }) => fromCountryCodeIso2
                    )
                )
            ];

            state.availableFromCountryCodes = availableFromCountryCodes;
            state.availableToCountryCodes = availableToCountryCodes;
            state.showError = false;
            state.internationalProductsGroupedByFromThenToCountry = internationalProductsGroupedByFromThenToCountry;
            state.isLoadingShippingProducts = false;
        }
    }
});

export const setDefaultValues = () => dispatch => {
    dispatch(setDefaultValuesOnBox());
    dispatch(fetchShippingProducts());
    dispatch(setSelectedToCountry({ selectedToCountry: defaultCountry }));
    dispatch(setSelectedFromCountry({ selectedFromCountry: defaultCountry }));
};

export const fetchShippingProducts = () => async (dispatch, getState) => {
    const { length, height, width, weight } = selectBox(getState());

    if (isLoadingShippingProducts(getState())) {
        return;
    }

    if (!selectIsBoxValid(getState())) {
        dispatch(
            setShippingProducts({
                internationalShippingCalculatorProducts: []
            })
        );
        return;
    }
    dispatch(beginFetchingShippingProducts());

    try {
        const response = await axiosWithTokenRefresh()
            .get(ENDPOINTS.INTERNATIONAL_SHIPPING_CALCULATOR, {
                params: {
                    lengthInMillimeters: length * 10,
                    heightInMillimeters: height * 10,
                    widthInMillimeters: width * 10,
                    weightInGrams: weight
                },
                ...axiosConfigs.authenticated
            })
            .then(finalizeResponse())
            .catch(handleError());

        dispatch(
            setShippingProducts({
                internationalShippingCalculatorProducts:
                    response.data.internationalShippingCalculatorProducts
            })
        );
    } catch (e) {
        dispatch(
            setShippingProducts({
                internationalShippingCalculatorProducts: []
            })
        );
        dispatch(setShowError());
    }
};

export const {
    updateFilter,
    setWeightUnit,
    beginFetchingShippingProducts,
    setShippingProducts,
    setDefaultValuesOnBox,
    setSelectedToCountry,
    setSelectedFromCountry,
    setWeightFilter,
    setShowError
} = shippingCalculatorSlice.actions;
export default shippingCalculatorSlice.reducer;
